/**
 * Order details modal component.
 */

import { Modal, Button, Spinner, Alert } from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/services/apiClient';
import { formatDate } from '@/utils/helpers';

interface OrderDetailsModalProps {
  show: boolean;
  onHide: () => void;
  orderId: number;
}

interface FulfillmentJob {
  status?: string;
}

interface OrderDetailsData {
  order_number?: string;
  status?: string;
  created_at?: string;
  device_ids?: string[];
  phone_verified?: boolean;
  fullfillment_jobs?: FulfillmentJob[];
}

export default function OrderDetailsModal({ show, onHide, orderId }: OrderDetailsModalProps) {
  const { data, isLoading, error } = useQuery<OrderDetailsData>({
    queryKey: ['orderDetails', orderId],
    queryFn: async () => {
      const response = await apiClient.getOrderDetails(orderId);
      if (response.error) {
        throw new Error(response.error);
      }
      return response.data as OrderDetailsData;
    },
    enabled: show && !!orderId,
  });

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Order Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading ? (
          <div className="text-center">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : error ? (
          <Alert variant="danger">Error loading order details</Alert>
        ) : data ? (
          <div>
            <h6 className="border-bottom pb-2 mb-3">Order Information</h6>
            <div className="row mb-3">
              <div className="col-md-4">
                <p className="mb-1 text-muted">Order Number:</p>
                <p className="fw-bold">{data.order_number || 'N/A'}</p>
              </div>
              <div className="col-md-4">
                <p className="mb-1 text-muted">Status:</p>
                <p className="fw-bold">{data.status || 'N/A'}</p>
              </div>
              <div className="col-md-4">
                <p className="mb-1 text-muted">Created:</p>
                <p className="fw-bold">{formatDate(data.created_at)}</p>
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-md-6">
                <p className="mb-1 text-muted">Device ID:</p>
                <p className="fw-bold">
                  {Array.isArray(data.device_ids) ? data.device_ids.join(', ') : 'N/A'}
                </p>
              </div>
              <div className="col-md-6">
                <p className="mb-1 text-muted">Phone Verified:</p>
                <p className="fw-bold">{data.phone_verified ? 'Yes' : 'No'}</p>
              </div>
            </div>
            {data.fullfillment_jobs && data.fullfillment_jobs.length > 0 && (
              <div className="mb-4">
                <h6 className="border-bottom pb-2 mb-3">Processing Jobs</h6>
                <ul>
                  {data.fullfillment_jobs.map((job: any, index: number) => (
                    <li key={index}>
                      <strong>Job {index + 1}:</strong> {job.status || 'Unknown'}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
